import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaCashRegister, FaCheck, FaPrint, FaSyncAlt, FaTimes } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import cajaService from '../../services/caja.service';
import { fechaDiaNegocioLocal } from '../../utils/fechaDiaNegocio';
import { BILLETERA_VIRTUAL_LABEL } from '../../utils/metodoPagoLabels';

const formatMoneda = (value) => `$${(parseFloat(value || 0)).toFixed(2)}`;

const formatFechaHora = (ms) => {
  if (!ms) return '—';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(ms));
  } catch {
    return '—';
  }
};

const CierreDiarioPanel = ({
  orgId,
  sucursalId,
  fecha: fechaProp,
  onFechaChange,
  canCerrar = true,
  canCorregir = false,
  dark = false,
  showHistorial = false,
  soloDiaActual = false,
  umbralAdminAuth = 500,
  onClose,
  onCierreDataSync
}) => {
  const hoyLocal = fechaDiaNegocioLocal();
  const [fecha, setFecha] = useState(
    () => fechaProp || hoyLocal
  );
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [efectivoDeclarado, setEfectivoDeclarado] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [motivoCorreccion, setMotivoCorreccion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [modalCorr, setModalCorr] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [corrigiendo, setCorrigiendo] = useState(false);

  useEffect(() => {
    if (fechaProp) setFecha(fechaProp);
  }, [fechaProp]);

  const suc = sucursalId || 'principal';

  const cargar = useCallback(async () => {
    if (!orgId || !fecha) return;
    setLoading(true);
    try {
      const body = await cajaService.obtenerCierreDia({ fecha, sucursalId: suc });
      setEstado({
        snapshot: body?.snapshot || {},
        snapshotVivo: body?.snapshotVivo || null,
        cierre: body?.cierre || null,
        cantidadMovimientos: body?.cantidadMovimientos ?? 0,
        movimientosPosterioresAlCierre: body?.movimientosPosterioresAlCierre ?? 0,
        cierreCerrado: body?.cierreCerrado === true || Boolean(body?.cierre)
      });
      onCierreDataSync?.(body);
    } catch (error) {
      setEstado(null);
      toast.error(error?.message || 'No se pudo cargar el cierre del día');
    } finally {
      setLoading(false);
    }
  }, [orgId, fecha, suc, onCierreDataSync]);

  const cargarHistorial = useCallback(async () => {
    if (!orgId || !showHistorial) return;
    setLoadingHistorial(true);
    try {
      const hace30 = new Date();
      hace30.setDate(hace30.getDate() - 30);
      const hoy = fechaDiaNegocioLocal();
      const body = await cajaService.listarCierres({
        sucursalId: suc,
        fechaDesde: fechaDiaNegocioLocal(hace30),
        fechaHasta: hoy,
        limit: 30
      });
      setHistorial(cajaService.ensureArray(body?.data || body));
    } catch {
      setHistorial([]);
    } finally {
      setLoadingHistorial(false);
    }
  }, [orgId, showHistorial, suc]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  const cierre = estado?.cierre;
  const yaCerrado = Boolean(estado?.cierreCerrado && cierre);
  const snapshot = yaCerrado
    ? (cierre?.snapshotAlCierre || estado?.snapshot || {})
    : (estado?.snapshot || {});
  const snapshotVivo = estado?.snapshotVivo;
  const movsPosteriores = Number(estado?.movimientosPosterioresAlCierre || 0);

  const declaradoNum = parseFloat(String(efectivoDeclarado).replace(',', '.')) || 0;
  const esperadoCierre = parseFloat(
    cierre?.efectivoEsperado ?? snapshot.efectivoEsperado ?? 0
  ) || 0;
  const diferenciaPreview = useMemo(() => {
    const base = yaCerrado ? esperadoCierre : parseFloat(snapshot.efectivoEsperado || 0);
    return Math.round((declaradoNum - base + Number.EPSILON) * 100) / 100;
  }, [declaradoNum, esperadoCierre, snapshot.efectivoEsperado, yaCerrado]);

  const requiereAuthAdminCierre = useMemo(() => {
    return !yaCerrado && Math.abs(diferenciaPreview) >= umbralAdminAuth && !canCorregir;
  }, [diferenciaPreview, umbralAdminAuth, canCorregir, yaCerrado]);

  const handleFecha = (value) => {
    if (soloDiaActual) return;
    setFecha(value);
    onFechaChange?.(value);
  };

  useEffect(() => {
    if (soloDiaActual && fecha !== hoyLocal) {
      setFecha(hoyLocal);
      onFechaChange?.(hoyLocal);
    }
  }, [soloDiaActual, hoyLocal, fecha, onFechaChange]);

  const abrirModalCorreccion = () => {
    setEfectivoDeclarado(String(cierre?.efectivoDeclarado ?? ''));
    setObservaciones(String(cierre?.observaciones || ''));
    setMotivoCorreccion('');
    setAdminEmail('');
    setAdminPassword('');
    setModalCorr(true);
  };

  const registrarCierre = async () => {
    if (!canCerrar || yaCerrado) {
      toast.warning(yaCerrado ? 'La caja de este día ya fue cerrada' : 'Sin permiso para cerrar caja');
      return;
    }
    if (declaradoNum < 0 || !Number.isFinite(declaradoNum)) {
      toast.warning('Ingresá un efectivo declarado válido');
      return;
    }
    if (Math.abs(diferenciaPreview) >= 0.01 && !observaciones.trim()) {
      toast.warning('Indicá una observación cuando hay diferencia de arqueo');
      return;
    }
    if (requiereAuthAdminCierre && (!adminEmail.trim() || !adminPassword)) {
      toast.warning(`Diferencia ≥ ${formatMoneda(umbralAdminAuth)}: se requiere autorización de administrador`);
      return;
    }
    setGuardando(true);
    try {
      const body = await cajaService.registrarCierreDia({
        fecha,
        sucursalId: suc,
        efectivoDeclarado: declaradoNum,
        observaciones,
        adminEmail: requiereAuthAdminCierre ? adminEmail.trim() : undefined,
        adminPassword: requiereAuthAdminCierre ? adminPassword : undefined
      });
      const diff = Number(body?.cierre?.diferenciaEfectivo ?? body?.cierre?.diferenciaArqueo);
      toast.success(Math.abs(diff) < 0.01 ? 'Cierre registrado.' : 'Cierre registrado (con diferencia).');
      setEfectivoDeclarado('');
      setObservaciones('');
      setAdminEmail('');
      setAdminPassword('');
      await cargar();
      await cargarHistorial();
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.warning(error?.message || 'Esta fecha ya tiene cierre registrado.');
        await cargar();
      } else if (error?.response?.data?.code === 'ADMIN_AUTH_REQUIRED' || error?.code === 'ADMIN_AUTH_REQUIRED') {
        toast.warning(error?.message || 'Se requiere autorización de administrador');
      } else {
        toast.error(error?.message || 'No se pudo registrar el cierre');
      }
    } finally {
      setGuardando(false);
    }
  };

  const corregirCierre = async (e) => {
    e.preventDefault();
    if (!motivoCorreccion.trim()) {
      toast.warning('Indicá el motivo de la corrección');
      return;
    }
    if (!canCorregir && (!adminEmail.trim() || !adminPassword)) {
      toast.warning('Se requieren credenciales de administrador');
      return;
    }
    setCorrigiendo(true);
    try {
      await cajaService.corregirCierreDia({
        fecha,
        sucursalId: suc,
        efectivoDeclarado: declaradoNum,
        observaciones,
        motivo: motivoCorreccion.trim(),
        adminEmail: canCorregir ? (adminEmail.trim() || undefined) : adminEmail.trim(),
        adminPassword: canCorregir ? (adminPassword || undefined) : adminPassword
      }, { sucursalId: suc });
      toast.success('Cierre corregido.');
      setModalCorr(false);
      setAdminEmail('');
      setAdminPassword('');
      setMotivoCorreccion('');
      await cargar();
      await cargarHistorial();
    } catch (error) {
      toast.error(error?.message || 'No se pudo corregir el cierre');
    } finally {
      setCorrigiendo(false);
    }
  };

  const imprimirResumen = () => {
    const snap = snapshot;
    const titulo = yaCerrado ? 'Cierre guardado' : 'Resumen vivo';
    const html = `
      <html><head><title>Cierre ${fecha}</title></head><body style="font-family:sans-serif;padding:16px">
      <h2>Cierre diario — ${fecha}</h2>
      <p>Sucursal: ${suc}</p>
      <p><em>${titulo}</em></p>
      <h3>Resumen</h3>
      <ul>
        <li>Ventas: ${formatMoneda(snap.ventasTotal)} (${snap.ventasCantidad || 0})</li>
        <li>Efectivo esperado: ${formatMoneda(snap.efectivoEsperado)}</li>
        <li>Tarjeta: ${formatMoneda(snap.tarjetaTotal)}</li>
        <li>${BILLETERA_VIRTUAL_LABEL}: ${formatMoneda(snap.mercadopagoTotal)}</li>
        <li>Transferencia: ${formatMoneda(snap.transferenciaTotal)}</li>
      </ul>
      ${cierre ? `<p>Declarado: ${formatMoneda(cierre.efectivoDeclarado)} · Diferencia: ${formatMoneda(cierre.diferenciaEfectivo)}</p>` : ''}
      </body></html>`;
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      toast.warning('Permití ventanas emergentes para imprimir');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const card = dark
    ? 'rounded-xl border border-white/10 bg-e0e3-surface/95 text-white'
    : 'rounded-xl border border-gray-200 bg-white text-gray-900';
  const muted = dark ? 'text-e0e3-muted' : 'text-gray-500';
  const label = dark ? 'text-e0e3-muted' : 'text-gray-600';

  const resumenGrid = (
    <div className="mb-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
      <div className={`rounded-lg p-2 ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>Ventas</div>
        <div className="font-black">{formatMoneda(snapshot.ventasTotal)}</div>
        <div className={`text-[10px] ${muted}`}>{snapshot.ventasCantidad || 0} ops.</div>
      </div>
      <div className={`rounded-lg p-2 ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>Tarjeta</div>
        <div className="font-black">{formatMoneda(snapshot.tarjetaTotal)}</div>
      </div>
      <div className={`rounded-lg p-2 ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>{BILLETERA_VIRTUAL_LABEL}</div>
        <div className="font-black">{formatMoneda(snapshot.mercadopagoTotal)}</div>
      </div>
      <div className={`rounded-lg p-2 ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>Ingresos manuales $</div>
        <div className="font-black">{formatMoneda(snapshot.ingresosManualesEfectivo)}</div>
      </div>
      <div className={`rounded-lg p-2 ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>Egresos manuales $</div>
        <div className="font-black">{formatMoneda(snapshot.egresosManualesEfectivo)}</div>
      </div>
      <div className={`rounded-lg p-2 ${dark ? 'bg-emerald-950/40 ring-1 ring-emerald-500/30' : 'bg-emerald-50 ring-1 ring-emerald-200'}`}>
        <div className={`text-[10px] uppercase ${muted}`}>Efectivo esperado</div>
        <div className="text-lg font-black">{formatMoneda(snapshot.efectivoEsperado)}</div>
      </div>
    </div>
  );

  return (
    <div className={`${card} p-4 shadow-sm`} data-pos-busqueda-pausada="1">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className={`flex items-center gap-2 text-lg font-black ${dark ? 'text-teal-300' : 'text-teal-800'}`}>
          <FaCashRegister />
          Cierre diario de caja
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={cargar} disabled={loading} className="rounded-lg px-2 py-1 text-xs font-bold opacity-80 hover:opacity-100">
            <FaSyncAlt className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={imprimirResumen} className="rounded-lg px-2 py-1 text-xs font-bold opacity-80 hover:opacity-100">
            <FaPrint />
          </button>
          {onClose && (
            <button type="button" onClick={onClose} className="rounded-lg p-1 opacity-80 hover:opacity-100">
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className={`mb-1 block text-xs font-bold uppercase ${label}`}>Fecha</label>
        <input
          type="date"
          value={fecha}
          onChange={(e) => handleFecha(e.target.value)}
          max={hoyLocal}
          disabled={soloDiaActual}
          className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-white/15 bg-black/40 text-white' : 'border-gray-300'} ${soloDiaActual ? 'opacity-70' : ''}`}
        />
        {soloDiaActual && (
          <p className={`mt-1 text-[10px] ${muted}`}>Resumen del día actual de tu sucursal</p>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : estado ? (
        <>
          {yaCerrado ? (
            <>
              <div className={`mb-3 rounded-lg border px-3 py-3 ${dark ? 'border-amber-500/40 bg-amber-950/30' : 'border-amber-200 bg-amber-50'}`}>
                <p className={`text-sm font-black ${dark ? 'text-amber-200' : 'text-amber-800'}`}>
                  La caja de este día ya fue cerrada.
                </p>
                <p className={`mt-1 text-xs ${muted}`}>
                  Cierre guardado — no es un resumen vivo. Los totales mostrados corresponden al momento del cierre.
                </p>
              </div>

              <div className={`mb-2 text-[10px] font-bold uppercase tracking-wide ${muted}`}>
                Cierre guardado
              </div>
              {resumenGrid}

              <div className={`mb-3 space-y-2 rounded-lg p-3 text-sm ${dark ? 'bg-black/25 ring-1 ring-white/10' : 'bg-teal-50/80 ring-1 ring-teal-100'}`}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <span className={muted}>Fecha cierre: </span>
                    <strong>{cierre.fecha || fecha}</strong>
                  </div>
                  <div>
                    <span className={muted}>Cerrado: </span>
                    <strong>{formatFechaHora(cierre.cerradoEn)}</strong>
                  </div>
                  <div>
                    <span className={muted}>Por: </span>
                    <strong>{cierre.cerradoPor?.nombre || cierre.cerradoPor?.email || '—'}</strong>
                  </div>
                  <div>
                    <span className={muted}>Estado: </span>
                    <strong>{cierre.estado || 'cerrado'}</strong>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2">
                  <div>Efectivo esperado (al cierre): <strong>{formatMoneda(cierre.efectivoEsperado)}</strong></div>
                  <div>Efectivo declarado: <strong>{formatMoneda(cierre.efectivoDeclarado)}</strong></div>
                  <div>
                    Diferencia:{' '}
                    <strong className={Math.abs(cierre.diferenciaEfectivo) < 0.01 ? 'text-emerald-500' : 'text-amber-500'}>
                      {formatMoneda(cierre.diferenciaEfectivo)}
                    </strong>
                  </div>
                  {cierre.observaciones && (
                    <div className={`mt-1 text-xs ${muted}`}>Obs.: {cierre.observaciones}</div>
                  )}
                </div>
              </div>

              {movsPosteriores > 0 && (
                <div className={`mb-3 rounded-lg px-3 py-2 text-sm ${dark ? 'bg-red-950/40 text-red-200' : 'bg-red-50 text-red-800'}`}>
                  Hay movimientos posteriores al cierre ({movsPosteriores}). Requiere revisión de administrador.
                  {snapshotVivo && (
                    <div className={`mt-1 text-xs ${muted}`}>
                      Resumen vivo actual: ventas {formatMoneda(snapshotVivo.ventasTotal)} · efectivo esperado {formatMoneda(snapshotVivo.efectivoEsperado)}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={abrirModalCorreccion}
                className={`flex min-h-11 w-full items-center justify-center rounded-xl border px-3 text-sm font-black ${dark ? 'border-amber-500/50 text-amber-200 hover:bg-amber-950/30' : 'border-amber-300 text-amber-800 hover:bg-amber-50'}`}
              >
                Solicitar reapertura / corrección
              </button>
            </>
          ) : (
            <>
              <div className={`mb-2 text-[10px] font-bold uppercase tracking-wide ${dark ? 'text-teal-300' : 'text-teal-700'}`}>
                Resumen vivo del día
              </div>
              {resumenGrid}

              {canCerrar ? (
                <div className="space-y-2">
                  <div>
                    <label className={`mb-1 block text-xs font-bold uppercase ${label}`}>Efectivo contado</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={efectivoDeclarado}
                      onChange={(e) => setEfectivoDeclarado(e.target.value)}
                      placeholder={Number(snapshot.efectivoEsperado || 0).toFixed(2)}
                      className={`w-full rounded-lg border px-3 py-2 text-lg font-black tabular-nums ${dark ? 'border-white/15 bg-black/40 text-white' : 'border-gray-300'}`}
                    />
                  </div>
                  <div className={`text-sm ${muted}`}>
                    Diferencia: <span className={Math.abs(diferenciaPreview) < 0.01 ? 'text-emerald-500 font-bold' : 'text-amber-500 font-bold'}>{formatMoneda(diferenciaPreview)}</span>
                  </div>
                  <textarea
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Observaciones (obligatorio si hay diferencia)"
                    rows={2}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-white/15 bg-black/40 text-white' : 'border-gray-300'}`}
                  />
                  {requiereAuthAdminCierre && (
                    <div className={`rounded-lg border p-3 text-sm ${dark ? 'border-amber-500/40 bg-amber-950/30' : 'border-amber-200 bg-amber-50'}`}>
                      <p className="mb-2 font-bold text-amber-600">
                        Diferencia ≥ {formatMoneda(umbralAdminAuth)}: autorización admin requerida
                      </p>
                      <input
                        type="email"
                        placeholder="Email administrador"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className={`mb-2 w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-white/15 bg-black/40 text-white' : 'border-gray-300'}`}
                      />
                      <input
                        type="password"
                        placeholder="Contraseña administrador"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-white/15 bg-black/40 text-white' : 'border-gray-300'}`}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={registrarCierre}
                    disabled={guardando}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-teal-600 font-black text-white hover:bg-teal-500 disabled:opacity-50"
                  >
                    {guardando ? <Spinner size="sm" /> : <FaCheck />}
                    Confirmar cierre
                  </button>
                </div>
              ) : (
                <p className={`text-sm ${muted}`}>Sin permiso para registrar cierre.</p>
              )}
            </>
          )}
        </>
      ) : (
        <p className={`text-sm ${muted}`}>No se pudieron cargar los datos.</p>
      )}

      {showHistorial && (
        <div className="mt-4 border-t pt-3">
          <h4 className={`mb-2 text-sm font-black ${label}`}>Historial reciente</h4>
          {loadingHistorial ? (
            <Spinner size="sm" />
          ) : historial.length === 0 ? (
            <p className={`text-xs ${muted}`}>Sin cierres registrados.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className={muted}>
                    <th className="py-1">Fecha</th>
                    <th>Esperado</th>
                    <th>Declarado</th>
                    <th>Diff.</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((row) => (
                    <tr key={row.id || row.fecha} className="border-t border-white/5">
                      <td className="py-1 font-semibold">{row.fecha}</td>
                      <td>{formatMoneda(row.efectivoEsperado)}</td>
                      <td>{formatMoneda(row.efectivoDeclarado)}</td>
                      <td className={Math.abs(row.diferenciaEfectivo) < 0.01 ? 'text-emerald-500' : 'text-amber-600'}>
                        {formatMoneda(row.diferenciaEfectivo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modalCorr && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <form onSubmit={corregirCierre} className={`w-full max-w-md rounded-xl p-4 ${dark ? 'bg-e0e3-surface text-white' : 'bg-white'}`}>
            <h4 className="mb-1 font-black">Corrección posterior al cierre</h4>
            <p className={`mb-3 text-xs ${muted}`}>
              Solo se ajusta efectivo declarado y observaciones. Los totales del cierre original no se recalculan.
            </p>
            <label className={`mb-1 block text-xs font-bold ${label}`}>Efectivo declarado corregido</label>
            <input
              type="number"
              value={efectivoDeclarado}
              onChange={(e) => setEfectivoDeclarado(e.target.value)}
              className={`mb-2 w-full rounded border px-3 py-2 ${dark ? 'border-white/15 bg-black/40' : ''}`}
            />
            <label className={`mb-1 block text-xs font-bold ${label}`}>Observaciones</label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              className={`mb-2 w-full rounded border px-3 py-2 ${dark ? 'border-white/15 bg-black/40' : ''}`}
              rows={2}
            />
            <label className={`mb-1 block text-xs font-bold ${label}`}>Motivo (obligatorio)</label>
            <textarea
              value={motivoCorreccion}
              onChange={(e) => setMotivoCorreccion(e.target.value)}
              placeholder="Motivo de la corrección o reapertura"
              className={`mb-2 w-full rounded border px-3 py-2 ${dark ? 'border-white/15 bg-black/40' : ''}`}
              rows={2}
              required
            />
            {!canCorregir && (
              <>
                <input
                  type="email"
                  placeholder="Email administrador"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className={`mb-2 w-full rounded border px-3 py-2 ${dark ? 'border-white/15 bg-black/40' : ''}`}
                  required
                />
                <input
                  type="password"
                  placeholder="Contraseña administrador"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className={`mb-2 w-full rounded border px-3 py-2 ${dark ? 'border-white/15 bg-black/40' : ''}`}
                  required
                />
              </>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={() => setModalCorr(false)} className="flex-1 rounded-lg border py-2 font-bold">Cancelar</button>
              <button type="submit" disabled={corrigiendo} className="flex-1 rounded-lg bg-teal-600 py-2 font-bold text-white">
                {corrigiendo ? 'Guardando…' : 'Confirmar corrección'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CierreDiarioPanel;
